/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useState
} from 'react';
import {useDispatch} from 'react-redux';
import {Typography} from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import cloneDeep from 'lodash.clonedeep';
import isUndefined from 'lodash.isundefined';
import {resolveDeviceName} from '@ion/core/src/entities/Devices';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {
  updateDeviceServices
} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  AccountLink,
  Device,
  Devices
} from '@ion/core/src/types/entities';
import {SmartSearchCriteria} from '@ion/core/src/types/services';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {
  FormItem
} from '@ion/dls/src/components/forms/FormItem';
import {
  FormSection
} from '@ion/dls/src/components/forms/FormSection';
import {Options} from '@ion/dls/src/components/menus/Dropdown';
import {SaveButton} from '@ion/dls/src/editors/controls/SaveButton';
import styles, {getClasses} from '@ion/dls/src/editors/editorStyles';
import SelectedEntityChips from '@ion/dls/src/editors/SelectedEntityChips';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';
import {SelectAllSearch} from '@ion/features/src/search/types';
import {useCurrentGlobalGroup} from '../../hooks/useCurrentGlobalGroup';
import {toggleUserPrompt} from '../../store/actions/user-prompt';
import {useAuthenticatedUser} from '../admin/users/useAuthenticatedUser';
import {
  getServices,
  validateServices
} from './DeviceServicesUtils';
import {getDeviceIdsSearch, getTotalRowsSearch} from './helpers';
import Services from './Services';

export interface ManageServicesEditorProps extends SelectAllSearch {
  onCloseDialog?: () => void;
  selectedDevices?: Devices;
  account?: AccountLink;
  search?: SmartSearchCriteria;
}

const styleOverrides = (theme: DLSTheme) => ({
  formPaper : {
    width                          : '75%',
    [theme.breakpoints.down('sm')] : {
      maxWidth : '100%'
    }
  },
  formDialogContent : {
    [theme.breakpoints.down('sm')] : {
      paddingTop : '0px'
    },
    '& > *:first-child' : {
      paddingTop : '0px'
    },
    overflow : 'hidden'
  },
  formSectionTitle : {
    ...dlsAlt('typography', 'cta3'),
    textTransform : 'uppercase',
    padding       : '10px 0'
  },
  chipContainer : {
    display   : 'flex',
    flexWrap  : 'wrap',
    maxHeight : '80px',
    overflowY : 'auto'
  },
  chipWrapper : {
    marginBottom : '10px',
    position     : 'relative'
  },
  status : {
    fontSize : '14px'
  },
  flexStyle : {
    display : 'flex'
  }
});

/**
 * The Manage Service editor with set fields and encapsulated logic
 * for handling the form's state, including validation.
 *
 * @param {Function} onCloseDialog: Callback function fired when the dialog requests to be closed
 * @param {Devices} selectedDevices: selected Devices to perform bulk operation
 */
export const ManageServicesEditor = ({
  onCloseDialog,
  account,
  selectedDevices,
  search,
  filters,
  totalRowsCount,
  areTotalRowsSelected = false
}: ManageServicesEditorProps) => {
  const dispatch                              = useDispatch();
  const dialogStyles                          = useExtendStyles(styles, styleOverrides);
  const classes                               = dialogStyles();
  const loginUserApplications                 = useAuthenticatedUser().applications;
  const {title: accountName}                  = useCurrentGlobalGroup();
  const [selectedChips, setSelectedChips]     = useState<Devices>([]);
  const [serviceOptions, setServiceOptions]   = useState<Options[]>([]);
  const [serviceInfo, setServiceInfo]         = useState<any>([]);
  const [taskName, setTaskName]               = useState<string>('');
  const [isTaskNameEmpty, setIsTaskNameEmpty] = useState<boolean>(false);
  const [loading, setLoading]                 = useState<boolean>(false);
  const [formSubmitted, setFormSubmitted]     = useState<boolean>(false);

  const onChipDelete = useCallback((selectedItem) => {
    setSelectedChips(selectedChips?.filter((item) => item.id !== selectedItem.id));
  }, [selectedChips]);

  const validateForm = function() {
    setIsTaskNameEmpty(!taskName);
    const {isValid, serviceData} = validateServices(serviceInfo);
    setServiceInfo(serviceData);
    if (taskName && isValid) {
      return true;
    }
    return false;
  };

  const getServiceName = useCallback((serviceName) => {
    const nameArr = serviceName.split(' ');
    if (nameArr?.length > 1) {
      return nameArr[0].toUpperCase();
    }
    return serviceName;
  }, []);

  const simulateResponses = useCallback((responses) => {
    const results = [];
    const errors = [];
    responses.forEach(response => {
      if (response && !isUndefined(response.errors)) {
        errors.push(response);
      } else {
        results.push(response);
      }
    });
    dispatch(toggleUserPrompt({
      message : serviceInfo?.length === errors?.length
        ? translate('admin-devices:services.manageServices.updateStatus.failed')
        : translate('admin-devices:services.manageServices.updateStatus.success')
    }));
  }, []);

  const onSubmit = function() {
    const valid = validateForm();
    if (valid) {
      (async function() {
        setLoading(true);
        setFormSubmitted(true);
        try {
          const statusResponses = await Promise.all(serviceInfo.map(async({service}) => {
            const {name, status} = service;
            const serviceName = getServiceName(name);
            const devices = areTotalRowsSelected ? getTotalRowsSearch(search) : getDeviceIdsSearch(selectedChips);
            return await updateDeviceServices({
              accountId     : entityId(account),
              taskNickName  : taskName,
              serviceName   : serviceName,
              serviceStatus : status,
              devices
            });
          }));
          simulateResponses(statusResponses);
        } catch (err) {
          error(err);
        }
        setLoading(false);
      })();
    }
  };

  const getServiceOptions = useCallback((services) => {
    const options = [];
    services?.map((item) => {
      options?.push({label: item, value: item});
    });
    setServiceOptions(options);
  }, []);

  useEffect(() => {
    const devices: Device[] = cloneDeep(selectedDevices).map((device: Device) => {
      device.name = resolveDeviceName(device);
      return device;
    });
    setSelectedChips(devices);
  }, [selectedDevices]);

  useEffect(() => {
    const enableServices = getServices(loginUserApplications)?.sort();
    getServiceOptions(enableServices);
  }, []);

  return (
    <Dialog open
      onBackdropClick={null}
      classes={getClasses(classes)}>
      <DialogTitle classes={{
        root : classes.formDialogTitle
      }}>
        <div className={classes.formTitle}>
          <div onClick={onCloseDialog} className={classes.closeButton}>
            <MaterialExitCloseIcon size={IconSize.medium}
              color={dlsColor('grey3')} />
          </div>
          <Typography component='h1' variant='h2'>
            {translate('admin-devices:menu.manageServices')}
          </Typography>
        </div>
      </DialogTitle>
      <form className={classes.form} onSubmit={onSubmit}>
        <div className={classes.formWrapperCtc}>
          <DialogContent className={classes.formDialogContent}>
            <FormSection title={translate('admin-devices:services.manageServices.selectedDevices')}>
              <FormItem fullWidth>
                <SelectedEntityChips noTextTransform
                  selected={selectedChips}
                  filters={filters}
                  accountName={accountName}
                  areTotalRowsSelected={areTotalRowsSelected}
                  onDelete={onChipDelete} />
              </FormItem>
            </FormSection>
            <Services serviceOptions={serviceOptions}
              areTotalRowsSelected={areTotalRowsSelected}
              totalRowsCount={totalRowsCount}
              serviceInfo={serviceInfo}
              minService={1}
              taskName={taskName}
              isTaskNameEmpty={isTaskNameEmpty}
              setTaskName={setTaskName}
              selectedDevices={selectedChips}
              onChange={function(value) {
                setServiceInfo(value);
              }} />
          </DialogContent>
        </div>
      </form>
      <DialogActions className={classes.formActions}>
        {!formSubmitted && (
          <TertiaryButton onClick={onCloseDialog}>
            {translate('common:editors.shared.cancel')}
          </TertiaryButton>
        )}
        <SaveButton onSave={!formSubmitted ? onSubmit : onCloseDialog}
          label={translate(`common:${formSubmitted ? 'done' : 'submit'}`)}
          disabled={loading}
          loading={loading} />
      </DialogActions>
    </Dialog>
  );
};

export default ManageServicesEditor;
